/**
 * Utility function to get nested property value.
 * Supports array syntax:
 * - 'tags.name' - returns array of all name values if tags is array, otherwise returns nested property value
 * - 'tags[0].name' - returns single name value from the specified index in tags array
 * - 'user.profile.name' - returns single value from standard nested property
 *
 * @returns {any | any[]} - Returns single value for indexed access or nested properties,
 *                          returns array of values for array access without [] notation
 */
export const getNestedPropertyValue = (
  obj: Record<string, any>,
  path: string,
): any => {
  // Check for array notation with index (e.g., tags[0].name)
  const indexedArrayMatch = path.match(/^(.+?)\[(\d+)\]\.(.+)$/);
  if (indexedArrayMatch) {
    const [, arrayPath, index, prop] = indexedArrayMatch;
    const arrayValue = arrayPath
      .split('.')
      .reduce((o, p) => (o ? o[p] : undefined), obj);

    if (Array.isArray(arrayValue) && arrayValue[Number(index)]) {
      return arrayValue[Number(index)][prop];
    }
    return undefined;
  }

  // Check for array notation without index (e.g., tags.name)
  const arrayMatch = path.match(/^(.+?)\.(.+)$/);
  if (arrayMatch && !indexedArrayMatch) {
    const [, arrayPath, prop] = arrayMatch;
    const arrayValue = arrayPath
      .split('.')
      .reduce((o, p) => (o ? o[p] : undefined), obj);

    if (Array.isArray(arrayValue)) {
      // Return array of all values for the specified property
      return arrayValue
        .map((item) =>
          item && typeof item === 'object' ? item[prop] : undefined,
        )
        .filter((value) => value !== undefined);
    }
  }

  // Standard nested property access
  return path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj);
};

/**
 * Utility function to set nested property value.
 */
export const setNestedPropertyValue = (
  obj: Record<string, any>,
  path: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  value: any,
): void => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const lastObj = keys.reduce((o, k) => (o[k] = o[k] || {}), obj);
  if (lastKey) lastObj[lastKey] = value;
};

export const isEmptyObject = (obj: object): boolean => {
  if (!obj) return true;

  const keys = Object.keys(obj);
  if (!keys.length) return true; // Empty at the top level

  return keys.every((key) => {
    const value = obj[key];

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return (
          !value.length ||
          value.every((val) =>
            typeof val === 'object' ? isEmptyObject(val) : val == null,
          )
        );
      }

      return isEmptyObject(value);
    }

    return value == null;
  });
};

export const deepEquals = (a: object, b: object): boolean => {
  if (a === b) return true;

  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const arrA = Array.isArray(a);
    const arrB = Array.isArray(b);

    if (arrA && arrB) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEquals(a[i], b[i])) return false;
      }
      return true;
    }

    if (arrA !== arrB) return false;

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (a instanceof RegExp && b instanceof RegExp) {
      return a.toString() === b.toString();
    }

    const keysA = Object.keys(a);
    if (keysA.length !== Object.keys(b).length) return false;

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!deepEquals(a[key], b[key])) return false;
    }

    return true;
  }

  return a !== a && b !== b; // NaN case
};

export const equals = (obj1: object, obj2: object, field?: string): boolean => {
  if (field) {
    return resolveFieldData(obj1, field) === resolveFieldData(obj2, field);
  }
  return deepEquals(obj1, obj2);
};

export const resolveFieldData = (data: object, field?: string): object => {
  if (!data || !field) {
    return null;
  }

  try {
    const value = data[field];
    if (isNotEmpty(value)) return value;
  } catch {
    /*
     * Performance optimization: https://github.com/primefaces/primereact/issues/4797
     * do nothing and continue to other methods to resolve field data
     */
  }

  if (field.includes('.')) {
    const fields = field.split('.');
    let value = data;

    for (const key of fields) {
      if (value == null) {
        return null;
      }
      value = value[key];
    }

    return value;
  }

  return data[field];
};

export const contains = (value: object, list: object[]): boolean => {
  if (value != null && list?.length) {
    for (const item of list) {
      if (equals(value, item)) return true;
    }
  }
  return false;
};

export const isNotEmpty = (value: object): boolean => {
  return !isEmpty(value);
};

export const isEmpty = (value: object | string): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (!(value instanceof Date) &&
      typeof value === 'object' &&
      Object.keys(value).length === 0)
  );
};

export const deepMerge = <T>(target: T, source: Partial<T>): T => {
  if (typeof target !== 'object' || target === null) {
    return source as T;
  }

  if (typeof source !== 'object' || source === null) {
    return target;
  }

  const merged: Record<string, any> = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetValue = (target as Record<string, any>)[key];
      const sourceValue = (source as Record<string, any>)[key];

      if (
        typeof targetValue === 'object' &&
        targetValue !== null &&
        typeof sourceValue === 'object' &&
        sourceValue !== null
      ) {
        merged[key] = deepMerge(targetValue, sourceValue);
      } else {
        merged[key] = sourceValue as T[keyof T];
      }
    }
  }

  return merged as T;
};
